import { AUTH_HEADERS } from '../constants/headers';
import type { RequestInput } from '../types';
import { HttpStatus } from '../types/httpStatus';
import { getAuthSessionEpoch } from './authSessionEpoch';
import { getAccessToken, isAccessTokenAuth0, setAuth0AccessToken } from './cookies';
import { isLastActiveExpired, SESSION_EVALUATED_DOM_EVENT } from './handleSession';
import { logger } from './logger';

export type Auth0TokenRefresher = () => Promise<string | null>;

let registeredRefresher: Auth0TokenRefresher | null = null;
let refreshInFlight: Promise<string | null> | null = null;
let refreshRunId = 0;

/**
 * Registers the browser-only Auth0 token refresher supplied by the React layer.
 * Must be called from a component mounted under `Auth0Provider`.
 */
export function registerAuth0TokenRefresher(refresher: Auth0TokenRefresher): void {
    registeredRefresher = refresher;
}

/** Clears the registered refresher when Auth0 React context unmounts. */
export function unregisterAuth0TokenRefresher(): void {
    registeredRefresher = null;
    // A refresh already awaiting Auth0 belongs to the unmounted context and cannot be
    // cancelled, so stop sharing it: the next mount starts its own.
    refreshInFlight = null;
}

/**
 * Returns the current Auth0 access cookie, or silently refreshes it when the
 * cookie was dropped but the Auth0 session is still valid.
 *
 * Does not call Auth0 when `isAccessTokenAuth0` is missing (guest / Janrain) or
 * when the local Auth0 session is expired, so inactivity logout cannot be undone via SSO.
 */
export async function ensureAuth0AccessToken(): Promise<string> {
    const existing = getAccessToken();
    if (existing) {
        return existing;
    }

    if (!isAccessTokenAuth0() || isLastActiveExpired()) {
        return '';
    }

    return (await refreshAuth0AccessToken()) ?? '';
}

/** True when client-side Auth0 owns the legacy access token and 401 refresh may run. */
export function isAuth0UnauthorizedRefreshEnabled(): boolean {
    if (globalThis.window === undefined) {
        return false;
    }

    return Boolean(registeredRefresher) && isAccessTokenAuth0();
}

/**
 * Proves the Auth0 refresh token is still valid before a mutation or in-tab
 * navigation continues. Access JWTs can remain valid after password reset, so
 * the refresh token is the authoritative remote-session check.
 *
 * Returns false when the refresher ended the local session. Transient failures
 * leave the existing access cookie in place and return true.
 */
export async function revalidateAuth0Session(): Promise<boolean> {
    if (globalThis.window === undefined || !isAuth0UnauthorizedRefreshEnabled() || isLastActiveExpired()) {
        return true;
    }

    const accessToken = await refreshAuth0AccessToken();
    return Boolean(accessToken || getAccessToken());
}

/**
 * Fetches a fresh Auth0 access token via the registered refresher, persists it in
 * the legacy cookie, and notifies session listeners. Concurrent callers share one
 * in-flight refresh (single-flight).
 *
 * A token that arrives after the session was cleared is discarded instead of written,
 * so a logout that lands mid-refresh cannot be undone by the pending refresh.
 */
export async function refreshAuth0AccessToken(): Promise<string | null> {
    if (!registeredRefresher) {
        return null;
    }

    if (refreshInFlight) {
        return refreshInFlight;
    }

    const refresher = registeredRefresher;
    const epochAtStart = getAuthSessionEpoch();
    refreshRunId += 1;
    const runId = refreshRunId;

    refreshInFlight = (async () => {
        try {
            const accessToken = await refresher();

            if (!accessToken) {
                return null;
            }

            if (getAuthSessionEpoch() !== epochAtStart || isLastActiveExpired()) {
                logger.info('Discarded Auth0 access token refreshed for an ended session', {});
                return null;
            }

            setAuth0AccessToken(accessToken);
            globalThis.window?.dispatchEvent(new CustomEvent(SESSION_EVALUATED_DOM_EVENT));

            return accessToken;
        } catch (error) {
            logger.error('Error refreshing Auth0 access token', error);
            return null;
        } finally {
            // Abandoned runs must not clear a newer one started after unregistration.
            if (runId === refreshRunId) {
                refreshInFlight = null;
            }
        }
    })();

    return refreshInFlight;
}

function isRetryableAuth0UnauthorizedResponse(
    fetchResponse: Response | Error | null,
    additionalSuccessCodes: number[]
): fetchResponse is Response {
    return (
        globalThis.window !== undefined &&
        !isLastActiveExpired() &&
        fetchResponse instanceof Response &&
        fetchResponse.status === HttpStatus.UNAUTHORIZED &&
        !additionalSuccessCodes.includes(fetchResponse.status) &&
        isAuth0UnauthorizedRefreshEnabled()
    );
}

/**
 * Demand-driven Auth0 recovery when the legacy cookie JWT is rejected.
 *
 * Runs only on the client, while the user is within the 30-minute `lastActive`
 * window, for a real 401 that is not an expected success code, while
 * `Auth0TokenSync` has registered a refresher and the access token is
 * Auth0-issued. Refreshes via the registered refresher, rewrites the cookie,
 * updates `Authorization`, and returns
 * request init for a single retry.
 *
 * Outside the inactivity threshold, `evaluateSession` logs the user out before
 * fetch; this path does not refresh. Returns `null` when no retry should run.
 */
export async function refreshInitAfterAuth0Unauthorized({
    fetchResponse,
    url: _url,
    init,
    additionalSuccessCodes,
}: {
    fetchResponse: Response | Error | null;
    url: RequestInput;
    init?: RequestInit;
    additionalSuccessCodes: number[];
}): Promise<RequestInit | undefined | null> {
    if (!isRetryableAuth0UnauthorizedResponse(fetchResponse, additionalSuccessCodes)) {
        return null;
    }

    const accessToken = await refreshAuth0AccessToken();
    if (!accessToken) {
        return null;
    }

    // Always set Authorization on retry; updateInitWithValidAuthHeader skips when the
    // outgoing request had no bearer token.
    const headers = new Headers(init?.headers ?? undefined);
    headers.set(AUTH_HEADERS.AUTHORIZATION, `Bearer ${accessToken}`);
    if (isAccessTokenAuth0()) {
        headers.set(AUTH_HEADERS.X_PETM_AUTH0_ENABLED, 'true');
    }

    return { ...init, headers };
}
